import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTrash, FaCloudUploadAlt, FaImages } from 'react-icons/fa';

const PhotoUpload = ({ cardId }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    fetchPhotos();
  }, [cardId]);

  const fetchPhotos = async () => {
    if (!cardId) return;

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/gallery/${cardId}`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = event.target.files || event.dataTransfer?.files;
    if (!files) return;
    
    setSelectedFiles(Array.from(files));
    setMessage('');
    setProgress({ current: 0, total: files.length, percent: 0 });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      // Delete from Laravel API first
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/gallery/${id}`);
      
      // Update local state immediately
      setPhotos(prevPhotos => prevPhotos.filter(photo => photo.id !== id));
      setMessage('Photo deleted successfully');

      // Then try to delete from WordPress if we have the media ID
      const photo = photos.find(p => p.id === id);
      if (photo?.wp_media_id) {
        const wpUsername = process.env.REACT_APP_WP_USERNAME;
        const wpAppPassword = process.env.REACT_APP_WP_APP_PASSWORD;

        if (wpUsername && wpAppPassword) {
          try {
            await axios.delete(
              `${process.env.REACT_APP_PAY_BASE_URL}/wp-json/wp/v2/media/${photo.wp_media_id}?force=true`,
              {
                headers: {
                  'Authorization': 'Basic ' + btoa(`${wpUsername}:${wpAppPassword}`)
                }
              }
            );
          } catch (wpError) {
            console.error('Error deleting from WordPress:', wpError);
            // Don't show this error to user since the photo is already deleted from our database
          }
        }
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage(error.response?.data?.message || 'Error deleting photo. Please try again.');
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setMessage('Please select files first');
      return;
    }

    if (!cardId) {
      setMessage('Card ID is not available');
      return;
    }

    setUploading(true);
    setProgress(prev => ({ ...prev, current: 0, percent: 0 }));
    const wpUsername = process.env.REACT_APP_WP_USERNAME;
    const wpAppPassword = process.env.REACT_APP_WP_APP_PASSWORD;

    if (!wpUsername || !wpAppPassword) {
      setMessage('WordPress credentials are not configured');
      setUploading(false);
      return;
    }

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append('file', file);

        // First upload to WordPress
        const wpResponse = await axios.post(`${process.env.REACT_APP_PAY_BASE_URL}/wp-json/wp/v2/media`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': 'Basic ' + btoa(`${wpUsername}:${wpAppPassword}`)
          }
        });

        // If WordPress upload successful, save to Laravel API
        if (wpResponse.data && wpResponse.data.source_url) {
          const galleryData = {
            image_url: wpResponse.data.source_url,
            title: file.name,
            card_id: cardId,
            wp_media_id: wpResponse.data.id // Store WordPress media ID
          };

          // Save to Laravel API
          const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/gallery`, galleryData);
          setPhotos(prevPhotos => [...prevPhotos, response.data]);
          
          const newProgress = i + 1;
          const percent = Math.round((newProgress / selectedFiles.length) * 100);
          setProgress(prev => ({ ...prev, current: newProgress, percent }));
        }
      }
      
      setMessage(`Successfully uploaded ${selectedFiles.length} photos!`);
      setSelectedFiles([]);
      // Reset file input
      document.getElementById('photo-upload').value = '';
      
    } catch (error) {
      console.error('Upload error:', error);
      setMessage('Error uploading photos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="space-y-4">
          <div className="flex flex-col gap-4">
            <div 
              className={`w-full border-2 border-dashed rounded-lg p-8 text-center
                transition-colors duration-200 cursor-pointer
                ${isDragging 
                  ? 'border-primary bg-primary/10' 
                  : 'border-gray-300 hover:border-primary hover:bg-gray-50'}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('photo-upload').click()}
            >
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="space-y-4">
                <div className="flex justify-center">
                  {selectedFiles.length > 0 ? (
                    <FaImages className="w-12 h-12 text-primary" />
                  ) : (
                    <FaCloudUploadAlt className="w-12 h-12 text-gray-400" />
                  )}
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-700">
                    {selectedFiles.length > 0 
                      ? `${selectedFiles.length} ${selectedFiles.length === 1 ? 'photo' : 'photos'} selected` 
                      : 'Drop photos here or click to upload'}
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    {selectedFiles.length > 0 
                      ? 'Click upload button below to start uploading'
                      : 'PNG, JPG, GIF up to 10MB'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <button 
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0}
                className={`px-8 py-3 rounded-md font-medium
                  transition-colors duration-200
                  ${uploading || selectedFiles.length === 0 
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-primary hover:bg-secondary text-highlight'}`}
              >
                {uploading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>
                      Uploading {progress.current}/{progress.total} ({progress.percent}%)
                    </span>
                  </span>
                ) : (
                  <span className="flex items-center">
                    <FaCloudUploadAlt className="mr-2 h-5 w-5" />
                    Upload Photos
                  </span>
                )}
              </button>
            </div>
          </div>
          
          {message && (
            <div className={`p-4 rounded-md ${
              message.includes('Error')
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
              {message}
            </div>
          )}
        </div>
      </div>

      {/* Photo List */}
      <div className="p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-lg font-semibold mb-4">Uploaded Photos</h2>
        {loading ? (
          <div className="text-center py-4 text-gray-500">Loading photos...</div>
        ) : photos.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No photos uploaded yet</div>
        ) : (
          <div className="columns-2 sm:columns-3 md:columns-4 gap-4 space-y-4">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group mb-4 break-inside-avoid">
                <img 
                  src={photo.image_url} 
                  alt={photo.title}
                  className="w-full rounded-lg"
                  loading="lazy"
                />
                <button
                  onClick={() => handleDelete(photo.id)}
                  className="absolute top-2 right-2 p-3 bg-red-500 text-white rounded-full 
                    opacity-100 sm:opacity-0 sm:group-hover:opacity-100 
                    transition-opacity duration-200 hover:bg-red-600
                    shadow-lg z-10 cursor-pointer"
                  title="Delete photo"
                >
                  <FaTrash size={16} />
                </button>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-200 rounded-lg pointer-events-none" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoUpload;
